import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMerchantSettings } from '@features/merchant-settings';
import { useTranslation } from 'react-i18next';
import useSWR, { type SWRConfiguration } from 'swr';
import { ForbiddenError, NotFoundError } from '@shared/api/errors/errors';
import { hasPermission } from '@entities/access';
import { createIdempotencyKey } from '@entities/withdrawal';
import { useAuth } from '@entities/auth';
import { AdminCommandDialog } from '@features/withdrawal';
import { useApiServices } from '@shared/lib/use-api-services';
import { formatTableDateTime } from '@shared/lib/format-date';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { cn } from '@shared/lib/utils';
import type {
  WithdrawalLimitChangeDto,
  WithdrawalStopDto,
  WithdrawalStopScope,
} from '@shared/api/contracts';
import {
  Button,
  Input,
  Label,
  Select,
  Skeleton,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@shared/ui';

const asList = <T,>(value: T[] | { data?: T[] } | undefined): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : value.data ?? [];
};

const STOP_SCOPES: WithdrawalStopScope[] = ['GLOBAL', 'USER', 'ASSET', 'NETWORK'];

const withdrawalAdminSwrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  shouldRetryOnError: (error) => !(error instanceof ForbiddenError) && !(error instanceof NotFoundError),
  errorRetryCount: 0,
};

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('w-full space-y-1.5', className)}>
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}


function MerchantSettingsEntry() {
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { openMerchantSettings, openMerchantPicker } = useMerchantSettings();
  const merchant = params.get('merchant');
  const highlight = params.get('highlight') ?? '';

  useEffect(() => {
    if (merchant) {
      openMerchantSettings(merchant, {
        pane: highlight || hash === '#auto-withdrawal' ? 'auto' : 'limits',
        highlight,
      });
    } else if (hash === '#auto-withdrawal') {
      openMerchantPicker();
    } else {
      return;
    }
    const next = new URLSearchParams(params);
    next.delete('merchant');
    next.delete('highlight');
    navigate({ pathname: '/withdrawal-control', search: next.toString(), hash: '' }, { replace: true });
  }, [hash, highlight, merchant, navigate, openMerchantPicker, openMerchantSettings, params]);

  return null;
}

export function WithdrawalControlPage() {
  const { t } = useTranslation('cabinet');
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (!hasPermission(user, 'platform.manage')) return <Navigate to="/balance" replace />;
  return (
    <div className="page-stack min-w-0 pb-8 sm:pb-10">
      <div className="grid grid-cols-[auto_1fr] items-start gap-2.5 rounded-lg border border-border bg-accent/40 p-3 sm:gap-3 sm:p-4">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0">
          <h1 className="font-sans text-base font-semibold text-foreground">{t('withdrawalControl.title')}</h1>
          <p className="mt-0.5 text-sm text-foreground">{t('withdrawalControl.intro')}</p>
        </div>
      </div>
      <MerchantSettingsEntry />
      <LimitQueueSection />
      <section className="surface-card">
        <div className="grid items-stretch gap-6 lg:grid-cols-2">
          <CoolingSection />
          <StopsSection className="border-t border-border pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0" />
        </div>
      </section>
    </div>
  );
}

function StopsSection({ className }: { className?: string }) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const services = useApiServices();
  const stops = useSWR('withdrawal-stops', () => services.withdrawals.listStops(), withdrawalAdminSwrConfig);
  const key = useRef(createIdempotencyKey());
  const [scope, setScope] = useState<WithdrawalStopScope>('GLOBAL');
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const rows = asList<WithdrawalStopDto>(stops.data).filter((item) => item.active !== false);

  const submit = async () => {
    setPending('create');
    try {
      await services.withdrawals.createStop(
        { scope, targetId: scope === 'GLOBAL' ? undefined : targetId.trim(), reason: reason.trim() || undefined },
        key.current,
      );
      key.current = createIdempotencyKey();
      setReason('');
      toast(t('withdrawalControl.stops.created'), 'success');
      await stops.mutate();
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'withdrawalControl.stops.error'), 'error');
    } finally {
      setPending(null);
    }
  };

  const release = async (id: string) => {
    setPending(id);
    const releaseKey = createIdempotencyKey();
    try {
      await services.withdrawals.releaseStop(id, undefined, releaseKey);
      toast(t('withdrawalControl.stops.released'), 'success');
      await stops.mutate();
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'withdrawalControl.stops.error'), 'error');
    } finally {
      setPending(null);
    }
  };

  return (
    <section className={cn('flex h-full flex-col space-y-3', className)}>
      <h2 className="text-lg font-semibold">{t('withdrawalControl.stops.title')}</h2>
      <p className="text-sm text-foreground">{t('withdrawalControl.stops.hint')}</p>
      <ul className="space-y-2">
        {rows.length === 0 ? <li className="text-sm text-foreground">{t('withdrawalControl.stops.empty')}</li> : null}
        {rows.map((stop) => (
          <li key={stop.id} className="flex flex-col gap-2 rounded-xl border border-border bg-background px-3 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between">
            <span className="min-w-0">
              <span className="font-medium">
                {stop.scope ? t(`withdrawalControl.stops.scope.${stop.scope}`) : stop.scopeKey}
              </span>
              {stop.targetId ? <span className="text-muted-foreground"> · {stop.targetId}</span> : null}
              {stop.reason ? <span className="block truncate text-xs text-muted-foreground">{stop.reason}</span> : null}
            </span>
            <Button type="button" size="sm" variant="outline" className="w-full shrink-0 sm:w-auto" loading={pending === stop.id} disabled={pending !== null && pending !== stop.id} onClick={() => void release(stop.id)}>
              {t('withdrawalControl.stops.release')}
            </Button>
          </li>
        ))}
      </ul>
      <div className="mt-auto flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:flex-wrap sm:items-end">
        <Field label={t('withdrawalControl.fields.scope')} className="sm:w-52">
          <Select value={scope} onValueChange={(value) => setScope(value as WithdrawalStopScope)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {STOP_SCOPES.map((item) => (
                <SelectItem key={item} value={item}>{t(`withdrawalControl.stops.scope.${item}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {scope === 'GLOBAL' ? null : (
          <Field label={t('withdrawalControl.fields.target')} className="sm:w-56">
            <Input value={targetId} placeholder={t('withdrawalControl.stops.target')} onChange={(event) => setTargetId(event.target.value)} />
          </Field>
        )}
        <Field label={t('withdrawalControl.fields.reason')} className="sm:w-64">
          <Input value={reason} placeholder={t('withdrawalControl.stops.reason')} onChange={(event) => setReason(event.target.value)} />
        </Field>
        <Button type="button" variant="destructive" className="w-full shrink-0 sm:w-auto" loading={pending === 'create'} disabled={pending !== null && pending !== 'create'} onClick={() => void submit()}>
          {t('withdrawalControl.stops.create')}
        </Button>
      </div>
    </section>
  );
}

function CoolingSection() {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const services = useApiServices();
  const settings = useSWR(
    'withdrawal-policy-settings',
    () => services.withdrawals.getPolicySettings(),
    withdrawalAdminSwrConfig,
  );
  const [minutes, setMinutes] = useState('');
  const [commandOpen, setCommandOpen] = useState(false);
  const currentMinutes = settings.data ? Math.round(settings.data.coolingPeriodSeconds / 60) : null;

  return (
    <section className="flex h-full flex-col space-y-3">
      <h2 className="text-lg font-semibold">{t('withdrawalControl.cooling.title')}</h2>
      <div className="flex items-center gap-2 text-sm text-foreground">
        <span>{t('withdrawalControl.cooling.currentLabel')}</span>
        {currentMinutes == null && settings.isLoading ? (
          <Skeleton className="h-4 w-24" />
        ) : (
          <span className="font-medium">
            {currentMinutes == null ? '—' : t('withdrawalControl.cooling.currentValue', { minutes: currentMinutes })}
          </span>
        )}
      </div>
      <p className="text-sm text-foreground">{t('withdrawalControl.cooling.hint')}</p>
      <div className="mt-auto flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-end">
        <Field label={t('withdrawalControl.fields.minutes')} className="sm:w-40">
          <Input
            inputMode="numeric"
            value={minutes}
            placeholder="0–43200"
            onChange={(event) => setMinutes(event.target.value.replace(/\D/g, ''))}
          />
        </Field>
        <Button type="button" className="w-full shrink-0 sm:w-auto" onClick={() => setCommandOpen(true)}>
          {t('withdrawalControl.cooling.save')}
        </Button>
      </div>
      <AdminCommandDialog
        open={commandOpen}
        title={t('withdrawalControl.cooling.save')}
        description={t('withdrawalControl.cooling.hint')}
        onOpenChange={setCommandOpen}
        onConfirm={async (command) => {
          const next = Number(minutes);
          if (!Number.isInteger(next) || next < 0 || next > 30 * 24 * 60 || !settings.data) {
            toast(t('withdrawalControl.cooling.invalid'), 'error');
            return;
          }
          try {
            await services.withdrawals.updatePolicySettings({
              version: settings.data.version,
              coolingPeriodSeconds: next * 60,
              secondFactor: command.secondFactor,
            });
            setCommandOpen(false);
            setMinutes('');
            toast(t('withdrawalControl.cooling.saved'), 'success');
            await settings.mutate();
          } catch (error) {
            toast(getApiErrorMessage(error, t, 'withdrawalControl.cooling.error'), 'error');
          }
        }}
      />
    </section>
  );
}

function LimitQueueSection() {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { user } = useAuth();
  const services = useApiServices();
  const queue = useSWR(
    'withdrawal-limit-changes',
    () => services.withdrawals.listLimitChanges(),
    withdrawalAdminSwrConfig,
  );
  const rows = asList<WithdrawalLimitChangeDto>(queue.data);
  const scopeLabel = (item: WithdrawalLimitChangeDto) => {
    if (item.scope === 'ASSET' && item.assetCode) {
      return t('withdrawalControl.queue.scopeAsset', { asset: item.assetCode });
    }
    if (item.scope === 'NETWORK' && item.networkCode) {
      return t('withdrawalControl.queue.scopeNetwork', { network: item.networkCode });
    }
    if (item.scope === 'ASSET_NETWORK' && item.assetCode && item.networkCode) {
      return t('withdrawalControl.queue.scopePair', { asset: item.assetCode, network: item.networkCode });
    }
    return t('withdrawalControl.queue.scopeAll');
  };
  const [busy, setBusy] = useState<{ id: string; kind: 'reject' | 'cancel' } | null>(null);
  const [approveId, setApproveId] = useState<string | null>(null);

  const act = async (id: string, kind: 'reject' | 'cancel') => {
    setBusy({ id, kind });
    try {
      if (kind === 'reject') await services.withdrawals.rejectLimitChange(id);
      else await services.withdrawals.cancelLimitChange(id);
      await queue.mutate();
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'withdrawalControl.limits.error'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="surface-card space-y-3">
      <h2 className="text-lg font-semibold">{t('withdrawalControl.queue.title')}</h2>
      <p className="text-sm text-foreground">{t('withdrawalControl.queue.hint')}</p>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">{t('withdrawalControl.queue.empty')}</p> : null}
      <ul className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:[&>:only-child]:col-span-2">
        {rows.map((item) => (
          <li key={item.id} className="flex h-full flex-col gap-3 rounded-xl border border-border bg-background px-3 py-3 text-sm">
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn(
                  'inline-flex rounded-md px-2 py-0.5 text-xs font-medium',
                  item.action === 'DELETE' ? 'bg-destructive/10 text-destructive' : 'bg-accent text-foreground',
                )}
                >
                  {t(item.action === 'DELETE' ? 'withdrawalControl.queue.removeLimits' : 'withdrawalControl.queue.setLimits')}
                </span>
                <p className="font-medium text-foreground">{t('withdrawalControl.queue.merchant', { id: item.merchantId })}</p>
              </div>
              <p className="text-foreground">{scopeLabel(item)}</p>
              {item.action === 'DELETE' ? (
                <p className="text-foreground">{t('withdrawalControl.queue.removeNote')}</p>
              ) : (
                <dl className="grid grid-cols-3 gap-2">
                  {([
                    ['withdrawalControl.fields.single', item.singleUsdt],
                    ['withdrawalControl.fields.daily', item.dailyUsdt],
                    ['withdrawalControl.fields.monthly', item.monthlyUsdt],
                  ] as const).map(([label, value]) => (
                    <div key={label} className="min-w-0 rounded-lg bg-muted/50 px-2 py-1.5">
                      <dt className="truncate text-[11px] leading-4 text-muted-foreground">{t(label)}</dt>
                      <dd className="truncate text-sm font-medium tabular-nums text-foreground">{formatLedgerAmount(value)}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <p className="text-xs text-muted-foreground">
                {t('withdrawalControl.queue.meta', {
                  author: item.authorId ?? '—',
                  time: formatTableDateTime(item.createdAt),
                  version: item.version,
                })}
              </p>
            </div>
            <div className="mt-auto flex shrink-0 flex-wrap gap-2">
              {item.authorId !== String(user?.id ?? '') ? (
                <Button type="button" size="sm" disabled={busy?.id === item.id} onClick={() => setApproveId(item.id)}>
                  {t('withdrawalControl.queue.approve')}
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="outline"
                loading={busy?.id === item.id && busy.kind === 'reject'}
                disabled={busy?.id === item.id && busy.kind !== 'reject'}
                onClick={() => void act(item.id, 'reject')}
              >
                {t('withdrawalControl.queue.reject')}
              </Button>
              {item.authorId === String(user?.id ?? '') ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  loading={busy?.id === item.id && busy.kind === 'cancel'}
                  disabled={busy?.id === item.id && busy.kind !== 'cancel'}
                  onClick={() => void act(item.id, 'cancel')}
                >
                  {t('withdrawalControl.queue.cancel')}
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      <AdminCommandDialog
        open={Boolean(approveId)}
        title={t('withdrawalControl.queue.approve')}
        reasonRequired={false}
        onOpenChange={(open) => !open && setApproveId(null)}
        onConfirm={async (command) => {
          if (!approveId) return;
          try {
            await services.withdrawals.approveLimitChange(approveId, command.secondFactor);
            setApproveId(null);
            await queue.mutate();
          } catch (error) {
            toast(getApiErrorMessage(error, t, 'withdrawalControl.limits.error'), 'error');
          }
        }}
      />
    </section>
  );
}
